import { useState, useMemo, useCallback, useRef, lazy, Suspense, useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useNavigate } from "react-router-dom";
import { Analytics } from '@vercel/analytics/react';
import "./App.css";
import { FIRM_DATABASE, STRATEGY_DEFAULTS, resolveFundedRules } from "./firmDatabase";
import { runMonteCarlo } from "./monteCarlo";
import { mcCall } from "./lib/mcClient";
import { beginSim, sleep } from "./lib/simTiming";
import { C } from "./lib/colors";
import { isPlanModified } from "./lib/format";
import { downloadJSON, exportPNG as exportPNGHelper } from "./lib/export";
import { LangProvider, useT } from "./components/LangContext";
import { AuthProvider, useAuth } from "./components/AuthContext";
import { UserPlanProvider } from "./components/UserPlanContext";
import Header from "./components/layout/Header";
import Footer from "./components/layout/Footer";
import ChamberTab from "./components/chamber/ChamberTab";
import LabTab from "./components/lab/LabTab";
import AccountView from "./components/account/AccountView";
import StepNav from "./components/layout/StepNav";
import TabCoach from "./components/shared/TabCoach";
import GuideDrawer from "./components/guide/GuideDrawer";
import ErrorBoundary from "./components/shared/ErrorBoundary";
import { Toaster as SonnerToaster } from "./components/ui/sonner";
import { hint } from "./lib/hints";
import { track, trackOnce } from "./lib/track";
import StrategyTab from "./components/strategy/StrategyTab";
import Terms from "./components/legal/Terms";
import Privacy from "./components/legal/Privacy";
import Cookies from "./components/legal/Cookies";
import Landing from "./components/landing/Landing";
import Guide from "./components/guide/Guide";
import ResetPassword from "./components/auth/ResetPassword";
import { BIZ_DEFAULT_CONFIG, BIZ_DEFAULT_NSIMS, BIZ_PRESETS } from "./lib/business";

// Lazy-loaded heavy tabs
const OracleTab = lazy(() => import("./components/oracle/OracleTab"));
const Glossary = lazy(() => import("./components/Glossary"));
const BusinessTab = lazy(() => import("./components/business/BusinessTab"));

// Archive noir loading fallback
const LoadingFallback = ({ message }) => (
  <div className="lazy-fallback" data-testid="lazy-fallback">
    <span className="lazy-fallback-text">{message}</span>
  </div>
);

// Root provides the language and auth context
function App() {
  return (
    <BrowserRouter>
      <LangProvider>
        <AuthProvider>
          <UserPlanProvider>
            <SonnerToaster richColors closeButton position="top-right" />
            <ErrorBoundary>
              <Routes>
                <Route path="/" element={<HomeRoute />} />
                <Route path="/app" element={<AppRoute />} />
                <Route path="/terms" element={<Terms />} />
                <Route path="/privacy" element={<Privacy />} />
                <Route path="/cookies" element={<Cookies />} />
                <Route path="/guide" element={<Guide />} />
                <Route path="/auth/reset" element={<ResetPassword />} />
              </Routes>
            </ErrorBoundary>
          </UserPlanProvider>
        </AuthProvider>
      </LangProvider>
    </BrowserRouter>
  );
}

// Logged-in users entering "/" are sent to the app; everyone else sees the landing.
function HomeRoute() {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (user) return <Navigate to="/app" replace />;
  return <Landing />;
}

// "/app" requires auth — UNLESS ?demo=1 is set, in which case we render a
// demo session (Fase 15-16 · Bloque 9). When ?demo=1 + logged in user, the
// flag is ignored and behaviour is normal.
function AppRoute() {
  const { user, loading } = useAuth();
  const params = new URLSearchParams(window.location.search);
  const isDemo = params.get("demo") === "1" && !user;
  if (loading) return null;
  if (!user && !isDemo) return <Navigate to="/" replace />;
  return <AppInner demoMode={isDemo} />;
}

function AppInner({ demoMode = false }) {
  const { t } = useT();
  const [activeTab, setActiveTab] = useState("chamber"); // chamber | strategy | lab | oracle | business | gloss | account
  const [previousTab, setPreviousTab] = useState("chamber"); // Fase 17B · Bloque 3 — for AccountView "← back"
  const [guideOpen, setGuideOpen] = useState(false); // Fase 17 · Bloque 4 — expandable TabCoach panel
  const [labAnalyzed, setLabAnalyzed] = useState(false); // Fase 15-16 · Bloque 2: step 3 progress
  const checklistRef = useRef(null); // Fase 15-16 · Bloque 2: stepper jump target
  const [marketFilter, setMarketFilter] = useState("ALL");
  const [selectedFirmId, setSelectedFirmId] = useState(null);
  const [selectedPlanId, setSelectedPlanId] = useState(null);
  const [planDraft, setPlanDraft] = useState(null);
  const [customUnlocked, setCustomUnlocked] = useState(false);
  const [strategy, setStrategy] = useState(STRATEGY_DEFAULTS);
  const [nSims, setNSims] = useState(STRATEGY_DEFAULTS.nSims);
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  // ─── Fase 13 — async CI on P(PASS) and stress test result ───
  const [ciResult, setCiResult] = useState(null);        // null | "computing" | {p5,p50,p95,...}
  const [stressResult, setStressResult] = useState(null); // null | "computing" | {grid,...}
  const [fundedPreview, setFundedPreview] = useState(null); // Fase 16B · Bloque 6: forced funded run for FREE teaser
  const [showAccountEditor, setShowAccountEditor] = useState(false);
  const [compareSlots, setCompareSlots] = useState([]);
  const [compareLoading, setCompareLoading] = useState(false);
  const [welcomeOpen, setWelcomeOpen] = useState(true);
  const [stripeBanner, setStripeBanner] = useState(null); // { type: 'success' | 'cancel', message: string }
  const [errorBanner, setErrorBanner] = useState(null);
  // Sep 2026 — business simulator state lives here so it survives tab switches.
  const [biz, setBiz] = useState(() => ({
    mix: BIZ_PRESETS[0].mix.map(m => ({ ...m, sizeMult: 1 })),
    config: { ...BIZ_DEFAULT_CONFIG },
    caps: {},
    nSims: BIZ_DEFAULT_NSIMS,
    seed: 20260902,
    results: null, robust: null, optimizer: null,
  }));

  const resultsRef = useRef(null);
  const compareRef = useRef(null);

  // ─── Stripe redirect handling ───
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const stripeStatus = params.get('stripe');
    
    if (stripeStatus === 'success') {
      setStripeBanner({ type: 'success', message: t('stripe_success_title') });
      window.history.replaceState({}, '', window.location.pathname);
      // Delay refresh to allow webhook to process
      setTimeout(() => {
        window.location.reload();
      }, 2000);
    } else if (stripeStatus === 'cancel') {
      setStripeBanner({ type: 'cancel', message: t('stripe_cancel_title') });
      window.history.replaceState({}, '', window.location.pathname);
      setTimeout(() => setStripeBanner(null), 3000);
    }
  }, [t]);

  // ─── Derivations ───
  const selectedFirm = useMemo(
    () => FIRM_DATABASE.find(f => f.id === selectedFirmId) || null,
    [selectedFirmId]
  );
  const presetPlan = useMemo(() => {
    if (!selectedFirm || !selectedPlanId) return null;
    return selectedFirm.plans.find(p => p.planId === selectedPlanId) || null;
  }, [selectedFirm, selectedPlanId]);
  const isModified = useMemo(() => isPlanModified(planDraft, presetPlan), [planDraft, presetPlan]);
  const regularFirms = useMemo(() => FIRM_DATABASE.filter(f => f.market !== "custom"), []);
  const customFirm = useMemo(() => FIRM_DATABASE.find(f => f.id === "custom"), []);
  const filteredRegular = useMemo(() => {
    if (marketFilter === "ALL") return regularFirms;
    if (marketFilter === "CUSTOM") return [];
    const map = { FUTURES: "futures", FOREX: "forex" };
    return regularFirms.filter(f => f.market === map[marketFilter]);
  }, [marketFilter, regularFirms]);
  const isCustomMode = selectedFirmId === "custom";

  // ─── Selection handlers ───
  const selectFirm = useCallback((id) => {
    setSelectedFirmId(id);
    setSelectedPlanId(null);
    setPlanDraft(null);
    setResults(null);
    setShowAccountEditor(false);
    setCustomUnlocked(id === "custom");
    if (id === "custom") {
      const firm = FIRM_DATABASE.find(f => f.id === "custom");
      if (firm?.plans?.[0]) {
        const plan = firm.plans[0];
        setSelectedPlanId(plan.planId);
        setPlanDraft({ ...plan, phase2: plan.phase2 ? { ...plan.phase2 } : undefined });
        setShowAccountEditor(true);
      }
    }
  }, []);

  const selectPlan = useCallback((plan) => {
    setSelectedPlanId(plan.planId);
    setPlanDraft({ ...plan, phase2: plan.phase2 ? { ...plan.phase2 } : undefined });
    setResults(null);
    // Fase 15-16 · Bloque 4 hint #1 — plan selected
    const firm = selectedFirm?.name || "";
    hint("plan_selected", {
      title: t("hint_plan_selected", { firm, plan: plan.label || plan.planId }),
      action: { label: t("hint_go"), onClick: () => setActiveTab("strategy") },
    });
  }, [selectedFirm, t]);

  const updateDraft = (patch) => {
    setPlanDraft(prev => {
      const next = { ...prev, ...patch };
      if (next.ddType === "static") next.floorLock = null;
      else if (!next.floorLock) next.floorLock = "at_capital";
      if (next.dailyLoss === null || next.dailyLoss === undefined) next.dailyLossIsFatal = false;
      if (!next.consistency) next.consistencyType = null;
      else if (!next.consistencyType) next.consistencyType = "vs_total";
      if (next.phases === 2 && !next.phase2) next.phase2 = { target: next.target, minDays: 0, maxDays: null };
      return next;
    });
  };
  const updatePhase2 = (patch) =>
    setPlanDraft(prev => ({ ...prev, phase2: { ...(prev.phase2 || {}), ...patch } }));

  // ─── Simulation handlers ───
  const handleTabClick = (tabId) => {
    setActiveTab(tabId);
  };

  // ─── Fase 17 · Bloque 3 — Scroll-to-top on EVERY tab change ─────────────
  // Runs after activeTab updates regardless of who triggered the change
  // (button click, CTA, hint action, `pf:nav` event, demo init, etc).
  // `instant` = jump without animation, matching the user spec.
  useEffect(() => {
    try {
      window.scrollTo({ top: 0, behavior: "instant" });
    } catch {
      window.scrollTo(0, 0);
    }
  }, [activeTab]);

  // Fase 17B · Bloque 3 — Track the last non-account tab so AccountView's
  // "← back" button returns the user to where they were before.
  useEffect(() => {
    if (activeTab !== "account") setPreviousTab(activeTab);
  }, [activeTab]);

  const handleRun = (opts = {}) => {
    if (!planDraft) return;
    setLoading(true);
    setResults(null);
    setCiResult(null);
    setStressResult(null);
    setActiveTab("oracle");
    const fundedRules = strategy.postPassEnabled
      ? resolveFundedRules(planDraft, selectedFirm?.id, strategy.fundedOverride || {})
      : null;
    const runOpts = opts.seed !== undefined ? { seed: opts.seed } : {};
    track("sim_run", { mode: strategy.mode, n: nSims });
    const simMs = beginSim();
    Promise.all([
      mcCall("run", planDraft, strategy, nSims, fundedRules, runOpts),
      sleep(simMs),
    ])
      .then(([r]) => {
        setResults(r);
        // Fase 15-16 · Bloque 4 hint #3 — simulation complete
        hint("sim_done", {
          title: t("hint_sim_done"),
          action: { label: t("hint_go"), onClick: () => {
            checklistRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
            checklistRef.current?.classList?.add("pf-flash");
            setTimeout(() => checklistRef.current?.classList?.remove("pf-flash"), 1500);
          }},
        });
      })
      .catch((e) => {
        console.error(e);
        setErrorBanner(String(e?.message || e));
        setTimeout(() => setErrorBanner(null), 6000);
      })
      .finally(() => setLoading(false));
  };

  // Fase 15-16 · Bloque 4 — global nav event (used by hints from deep components)
  useEffect(() => {
    const handler = (e) => { if (e.detail?.to) setActiveTab(e.detail.to); };
    window.addEventListener("pf:nav", handler);
    return () => window.removeEventListener("pf:nav", handler);
  }, []);

  // Fase 15-16 · Bloque 9 — demo mode auto-init
  // On first mount in demo mode: select Topstep 50K, load the example bootstrap
  // data, and trigger a simulation so the user lands directly on RESULTS.
  // Fase 18 · Demo en blanco — la demo NO precarga nada: el usuario elige
  // firma, carga su estrategia y simula él mismo, empezando en el paso ①
  // (mismo estado que un usuario recién registrado, pero sin login). Solo
  // se registra el evento de analítica.
  const demoInitRef = useRef(false);
  useEffect(() => {
    if (!demoMode || demoInitRef.current) return;
    demoInitRef.current = true;
    track("demo_started");
  }, [demoMode]);

  // Fase 14 · Bloque 1a: rerun with a new seed (keeps everything else)
  const handleNewSeed = useCallback(() => {
    handleRun({ seed: (Date.now() ^ (Math.random() * 1e9)) >>> 0 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [planDraft, strategy, selectedFirm, nSims]);

  // ─── Fase 13 — kick off async CI after main results paint ───
  // Now runs in the Web Worker (Fase 14 · Bloque 2) so it never blocks the UI.
  useEffect(() => {
    if (!results || !planDraft) return;
    if (strategy.mode !== "bootstrap") { setCiResult(null); return; }
    setCiResult("computing");
    let cancelled = false;
    mcCall("ci", planDraft, strategy, { replicas: 20, simsPerReplica: 1500 })
      .then((ci) => { if (!cancelled) setCiResult(ci); })
      .catch((e) => {
        console.error("CI computation failed:", e);
        if (!cancelled) setCiResult(null);
      });
    return () => { cancelled = true; };
  }, [results, planDraft, strategy]);

  // Fase 16B · Bloque 6 — Forced funded preview for FREE users (teaser).
  // Runs ONLY when funded was NOT enabled in the original run, so we don't
  // duplicate work. PRO/Annual users see the real funded panel and skip this.
  useEffect(() => {
    if (!results || !planDraft) { setFundedPreview(null); return; }
    if (strategy.postPassEnabled) { setFundedPreview(null); return; } // already in results
    setFundedPreview("computing");
    let cancelled = false;
    import("./firmDatabase").then(({ resolveFundedRules }) => {
      const fr = resolveFundedRules(planDraft, selectedFirm?.id, strategy.fundedOverride || {});
      const teaseStrategy = { ...strategy, postPassEnabled: true, postPassHorizonMonths: 6 };
      mcCall("run", planDraft, teaseStrategy, 6000, fr, { seed: results.seed || 12345 })
        .then(r => { if (!cancelled) setFundedPreview(r.postPass || null); })
        .catch(() => { if (!cancelled) setFundedPreview(null); });
    }).catch(() => { /* firmDatabase not found — skip teaser */ });
    return () => { cancelled = true; };
  }, [results, planDraft, strategy, selectedFirm]);

  const handleRunStress = useCallback(() => {
    if (!planDraft || !results) return;
    setStressResult("computing");
    const simMs = beginSim();
    Promise.all([
      mcCall("stress", planDraft, strategy, { simsPerCell: 3000 }),
      sleep(simMs),
    ])
      .then(([s]) => {
        setStressResult(s);
        // Fase 15-16 · Bloque 4 hint #4 — stress margins positive
        const wrOk = s.marginWRpp > 0;
        const lossOk = s.marginLossPct > 0;
        if (wrOk && lossOk) {
          hint("stress_margin", { title: t("hint_stress_margin") });
        }
      })
      .catch((e) => {
        console.error("Stress test failed:", e);
        setStressResult(null);
      });
  }, [planDraft, results, strategy, t]);

  const addToCompare = () => {
    if (!planDraft || !selectedFirm || compareSlots.length >= 4) return;
    setCompareSlots(s => [...s, {
      id: Date.now() + Math.random(),
      firmId: selectedFirm.id,
      firmName: selectedFirm.name,
      planLabel: planDraft.label + (isModified ? " · mod" : ""),
      plan: { ...planDraft, phase2: planDraft.phase2 ? { ...planDraft.phase2 } : undefined },
      results: null,
    }]);
  };
  const [compareSeed, setCompareSeed] = useState(null);
  const removeFromCompare = (id) => setCompareSlots(s => s.filter(x => x.id !== id));
  const clearCompare = () => { setCompareSlots([]); setCompareSeed(null); };
  const runAllCompare = (opts = {}) => {
    if (compareSlots.length < 2) return;
    setCompareLoading(true);
    // Fase 14 · Bloque 6: Common Random Numbers — one seed for the whole compare run.
    // Same randomness against different rules ⇒ differences are 100% from the rules.
    const seed = Number.isFinite(opts.seed)
      ? (opts.seed >>> 0)
      : ((Date.now() ^ (Math.random() * 1e9)) >>> 0);
    setCompareSeed(seed);
    const simMs = beginSim();
    let computed = null;
    try {
      computed = compareSlots.map(slot => {
        const fr = strategy.postPassEnabled
          ? resolveFundedRules(slot.plan, slot.firmId, strategy.fundedOverride || {})
          : null;
        return { ...slot, results: runMonteCarlo(slot.plan, strategy, nSims, fr, { seed }) };
      });
    } catch (e) { console.error(e); }
    setTimeout(() => {
      if (computed) setCompareSlots(computed);
      setCompareLoading(false);
    }, simMs);
  };

  // ─── Export handlers ───
  const exportJSON = () => {
    if (!results) return;
    downloadJSON(`propforge-${selectedFirm?.id}-${planDraft?.planId}-${Date.now()}.json`, {
      exportedAt: new Date().toISOString(),
      firm: { id: selectedFirm?.id, name: selectedFirm?.name, subtitle: selectedFirm?.subtitle },
      plan: planDraft, strategy, nSims, results,
    });
  };
  const exportPNG = (ref, name) => exportPNGHelper(ref, `${name}-${Date.now()}`, C.ink);
  const exportCompareJSON = () => {
    if (!compareSlots.length) return;
    downloadJSON(`propforge-compare-${Date.now()}.json`, {
      exportedAt: new Date().toISOString(), strategy, nSims,
      slots: compareSlots.map(s => ({ firmId: s.firmId, firmName: s.firmName,
        planLabel: s.planLabel, plan: s.plan, results: s.results })),
    });
  };

  return (
    <div style={{ minHeight: "100vh", color: C.linen }} data-testid="app-root">
      <Header />

      {/* Fase 17B · Bloque 1 — Steps are the navigation */}
      <StepNav
        activeTab={activeTab}
        hasPlan={!!planDraft}
        hasStrategy={strategy.mode === "simple"
          || (Array.isArray(strategy.bootstrapData) && strategy.bootstrapData.length > 0)}
        labAnalyzed={labAnalyzed}
        hasResults={!!results}
        onNavigate={(id) => handleTabClick(id)}
        onResultsReclick={() => {
          checklistRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
          checklistRef.current?.classList?.add("pf-flash");
          setTimeout(() => checklistRef.current?.classList?.remove("pf-flash"), 1500);
        }}
        guideOpen={guideOpen}
        onToggleGuide={() => setGuideOpen(o => !o)}
      />

      {guideOpen && (
        <div data-testid="tabs-guide-panel"
             style={{ borderBottom: `1px solid ${C.dust}`,
                      background: "rgba(11, 15, 16, 0.6)" }}>
          <div style={{ maxWidth: 1100, margin: "0 auto", padding: "12px 16px" }}>
            <TabCoach key={activeTab}
                      tabId={activeTab}
                      onCta={() => {
                        if (activeTab === "chamber") setActiveTab("strategy");
                        else if (activeTab === "strategy") setActiveTab("lab");
                        else if (activeTab === "lab") {
                          if (planDraft && (strategy.mode === "simple" || strategy.bootstrapData?.length)) {
                            handleRun();
                          } else if (!planDraft) {
                            setActiveTab("chamber");
                          } else {
                            setActiveTab("strategy");
                          }
                        } else if (activeTab === "business") {
                          const btn = document.querySelector('[data-testid="biz-run"]');
                          btn?.scrollIntoView({ behavior: "smooth", block: "center" });
                          btn?.focus();
                        } else if (activeTab === "oracle") {
                          checklistRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
                          checklistRef.current?.classList?.add("pf-flash");
                          setTimeout(() => checklistRef.current?.classList?.remove("pf-flash"), 1500);
                        }
                      }} />
          </div>
        </div>
      )}

      {stripeBanner && (
        <div className={`stripe-banner stripe-banner--${stripeBanner.type}`} data-testid="stripe-banner">
          {stripeBanner.message}
        </div>
      )}

      {demoMode && (
        <div data-testid="demo-banner"
             style={{ padding: "10px 16px",
                       background: `${C.brass}1A`,
                       borderBottom: `1px solid ${C.brass}`,
                       color: C.linen, fontFamily: "var(--plex)", fontSize: 12.5,
                       textAlign: "center", letterSpacing: 0.05 }}>
          {t("demo_banner")} {" "}
          <a href="/" data-testid="demo-signup-cta"
             style={{ color: C.brass, textDecoration: "underline", marginLeft: 8,
                       fontWeight: 600 }}>
            {t("demo_banner_cta")}
          </a>
        </div>
      )}

      {/* Fase 17 · Bloque 2 — FirstRunWizard eliminado */}

      {errorBanner && (
        <div data-testid="error-banner"
             style={{ padding: "10px 16px", margin: "10px 0",
                       border: "1px solid #C15733", background: "rgba(193, 87, 51, 0.10)",
                       color: "#C15733", fontFamily: "var(--mono)", fontSize: 12 }}>
          ⚠ {errorBanner}
        </div>
      )}

      {activeTab === "chamber" && (
        <ChamberTab
          marketFilter={marketFilter} setMarketFilter={setMarketFilter}
          welcomeOpen={welcomeOpen} setWelcomeOpen={setWelcomeOpen}
          filteredRegular={filteredRegular} customFirm={customFirm}
          selectedFirm={selectedFirm} selectedFirmId={selectedFirmId} selectFirm={selectFirm}
          selectedPlanId={selectedPlanId} selectPlan={selectPlan}
          isCustomMode={isCustomMode} planDraft={planDraft} isModified={isModified}
          setActiveTab={setActiveTab}
        />
      )}

      {activeTab === "strategy" && (
        <StrategyTab
          planDraft={planDraft} strategy={strategy} setStrategy={setStrategy}
          selectedFirm={selectedFirm} isCustomMode={isCustomMode} isModified={isModified}
          showAccountEditor={showAccountEditor} setShowAccountEditor={setShowAccountEditor}
          customUnlocked={customUnlocked} setCustomUnlocked={setCustomUnlocked}
          presetPlan={presetPlan} selectPlan={selectPlan}
          updateDraft={updateDraft} updatePhase2={updatePhase2}
          nSims={nSims} setNSims={setNSims} loading={loading} handleRun={handleRun}
          compareSlots={compareSlots} addToCompare={addToCompare}
          removeFromCompare={removeFromCompare} clearCompare={clearCompare}
          runAllCompare={runAllCompare} compareLoading={compareLoading}
          setActiveTab={setActiveTab}
        />
      )}

      {activeTab === "lab" && (
        <>
          {(() => { trackOnce("lab_viewed"); return null; })()}
          <LabTab strategy={strategy} onAnalyzed={() => setLabAnalyzed(true)} setActiveTab={setActiveTab} />
        </>
      )}

      {activeTab === "oracle" && (
        <Suspense fallback={<LoadingFallback message="loading results..." />}>
          <OracleTab
            results={results} loading={loading}
            ciResult={ciResult} stressResult={stressResult} onRunStress={handleRunStress}
            onNewSeed={handleNewSeed} strategy={strategy} fundedPreview={fundedPreview}
            planDraft={planDraft} selectedFirm={selectedFirm} isModified={isModified}
            resultsRef={resultsRef} compareRef={compareRef} compareSlots={compareSlots} compareSeed={compareSeed}
            checklistRef={checklistRef} labAnalyzed={labAnalyzed}
            exportJSON={exportJSON} exportPNG={exportPNG} exportCompareJSON={exportCompareJSON}
            setActiveTab={setActiveTab}
          />
        </Suspense>
      )}

      {activeTab === "business" && (
        <Suspense fallback={<LoadingFallback message="loading business simulator..." />}>
          <BusinessTab biz={biz} setBiz={setBiz} strategy={strategy} setActiveTab={setActiveTab} />
        </Suspense>
      )}

      {activeTab === "gloss" && (
        <div className="pf-tab-body fg-fadein" data-testid="pane-gloss">
          <div className="pf-tab-head">
            <div className="pf-tab-watermark">05</div>
            <div className="pf-tab-kicker">05 · help</div>
            <h2 className="pf-tab-title">{t("tab_help_title")}</h2>
            <div className="pf-tab-hint">{t("tab_help_hint")}</div>
          </div>
          <Suspense fallback={<LoadingFallback message="opening the archive..." />}>
            <Glossary />
          </Suspense>
        </div>
      )}

      {activeTab === "account" && (
        <AccountView setActiveTab={setActiveTab} previousTab={previousTab} />
      )}

      <Footer />
      {/* Fase 17B · Bloque 2 — Right-side drawer with full guide */}
      <GuideDrawer />
    </div>
  );
}

export default App;
